/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAuth } from '../components/Auth'
import {
  Badge,
  PageHeader,
  State,
} from '../components/UI'
import {
  api,
  businessPath,
  dateTime,
  money,
} from '../lib/api'


type Delivery = {
  id: string
  branch_id: string
  customer_id: string
  sale_id?: string | null
  address: string
  delivery_fee: string
  rider_driver_name?: string | null
  rider_driver_phone?: string | null
  tracking_reference: string
  status: string
  notes?: string | null
  created_at: string
  updated_at: string
}


type History = {
  id: string
  previous_status?: string | null
  new_status: string
  changed_by: string
  changed_at: string
  note?: string | null
}


type Customer = {
  id: string
  name: string
  phone?: string | null
  email?: string | null
  address?: string | null
}


type Sale = {
  id: string
  receipt_number: string
  customer_id?: string | null
  branch_id: string
  total: string
}


type Branch = {
  id: string
  name: string
}


type FormState = {
  branch_id: string
  customer_id: string
  sale_id: string
  address: string
  delivery_fee: string
  tracking_reference: string
  rider_driver_name: string
  rider_driver_phone: string
  notes: string
}


const emptyForm: FormState = {
  branch_id: '',
  customer_id: '',
  sale_id: '',
  address: '',
  delivery_fee: '0',
  tracking_reference: '',
  rider_driver_name: '',
  rider_driver_phone: '',
  notes: '',
}


const statuses = [
  'PENDING',
  'IN_TRANSIT',
  'DELIVERED',
  'CANCELLED',
]


export default function DeliveryPage() {
  const { id } = useParams<{ id: string }>()
  const { workspace } = useAuth()

  const branches =
    (
      workspace as typeof workspace & {
        accessible_branches?: Branch[]
      }
    ).accessible_branches ??
    (workspace.main_branch ? [workspace.main_branch] : [])

  const [items, setItems] = useState<Delivery[]>([])
  const [item, setItem] = useState<Delivery | null>(null)

  const [customers, setCustomers] = useState<Customer[]>([])
  const [sales, setSales] = useState<Sale[]>([])
  const [history, setHistory] = useState<History[]>([])

  const [form, setForm] = useState<FormState>(emptyForm)
  const [editForm, setEditForm] =
    useState<FormState>(emptyForm)

  const [statusFilter, setStatusFilter] = useState('')
  const [branchFilter, setBranchFilter] = useState('')
  const [customerFilter, setCustomerFilter] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [transitionNote, setTransitionNote] = useState('')
  const [editing, setEditing] = useState(false)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const [error, setError] = useState('')
  const [message, setMessage] = useState('')


  const branchName = (branchId: string) =>
    branches.find((branch) => branch.id === branchId)?.name ??
    branchId


  const customer = (customerId: string) =>
    customers.find((entry) => entry.id === customerId)


  const sale = (saleId?: string | null) =>
    sales.find((entry) => entry.id === saleId)


  const customerSales = useMemo(
    () =>
      sales.filter(
        (entry) =>
          !form.customer_id ||
          entry.customer_id === form.customer_id,
      ),
    [sales, form.customer_id],
  )


  const loadReferenceData = async () => {
    const [customerRows, saleRows] = await Promise.all([
      api<Customer[]>(businessPath('/customers')),
      api<Sale[]>(businessPath('/sales')),
    ])

    setCustomers(customerRows)
    setSales(saleRows)
  }


  const loadList = async () => {
    setLoading(true)
    setError('')

    try {
      const query = new URLSearchParams()

      if (statusFilter) {
        query.set('status', statusFilter)
      }

      if (branchFilter) {
        query.set('branch', branchFilter)
      }

      if (customerFilter) {
        query.set('customer', customerFilter)
      }

      if (startDate) {
        query.set('start_date', startDate)
      }

      if (endDate) {
        query.set('end_date', endDate)
      }

      const suffix = query.toString()
        ? `?${query.toString()}`
        : ''

      const [deliveryRows] = await Promise.all([
        api<Delivery[]>(
          businessPath(`/deliveries${suffix}`),
        ),
        loadReferenceData(),
      ])

      setItems(deliveryRows)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load deliveries.',
      )
    } finally {
      setLoading(false)
    }
  }


  const loadDetail = async () => {
    if (!id) return

    setLoading(true)
    setError('')

    try {
      const [
        deliveryRow,
        historyRows,
        customerRows,
        saleRows,
      ] = await Promise.all([
        api<Delivery>(
          businessPath(`/deliveries/${id}`),
        ),
        api<History[]>(
          businessPath(`/deliveries/${id}/history`),
        ),
        api<Customer[]>(businessPath('/customers')),
        api<Sale[]>(businessPath('/sales')),
      ])

      setItem(deliveryRow)
      setHistory(historyRows)
      setCustomers(customerRows)
      setSales(saleRows)

      setEditForm({
        branch_id: deliveryRow.branch_id,
        customer_id: deliveryRow.customer_id,
        sale_id: deliveryRow.sale_id ?? '',
        address: deliveryRow.address,
        delivery_fee: deliveryRow.delivery_fee,
        tracking_reference:
          deliveryRow.tracking_reference,
        rider_driver_name:
          deliveryRow.rider_driver_name ?? '',
        rider_driver_phone:
          deliveryRow.rider_driver_phone ?? '',
        notes: deliveryRow.notes ?? '',
      })
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load delivery.',
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    if (id) {
      void loadDetail()
    } else {
      void loadList()
    }
  }, [id])


  const create = async (event: FormEvent) => {
    event.preventDefault()

    if (
      !form.branch_id ||
      !form.customer_id ||
      !form.address.trim() ||
      !form.tracking_reference.trim()
    ) {
      setError(
        'Branch, customer, address and tracking reference are required.',
      )
      return
    }

    setBusy(true)
    setError('')
    setMessage('')

    try {
      const created = await api<Delivery>(
        businessPath('/deliveries'),
        {
          method: 'POST',
          body: JSON.stringify({
            branch_id: form.branch_id,
            customer_id: form.customer_id,
            sale_id: form.sale_id || null,
            address: form.address.trim(),
            delivery_fee: form.delivery_fee || '0',
            tracking_reference:
              form.tracking_reference.trim(),
            rider_driver_name:
              form.rider_driver_name.trim() || null,
            rider_driver_phone:
              form.rider_driver_phone.trim() || null,
            notes: form.notes.trim() || null,
          }),
        },
      )

      setItems((current) => [
        created,
        ...current,
      ])

      setForm(emptyForm)
      setMessage('Delivery created successfully.')
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to create delivery.',
      )
    } finally {
      setBusy(false)
    }
  }


  const saveEdit = async (event: FormEvent) => {
    event.preventDefault()

    if (!item) return

    if (
      !editForm.address.trim() ||
      !editForm.tracking_reference.trim()
    ) {
      setError(
        'Address and tracking reference are required.',
      )
      return
    }

    setBusy(true)
    setError('')
    setMessage('')

    try {
      const updated = await api<Delivery>(
        businessPath(`/deliveries/${item.id}`),
        {
          method: 'PATCH',
          body: JSON.stringify({
            address: editForm.address.trim(),
            delivery_fee:
              editForm.delivery_fee || '0',
            rider_driver_name:
              editForm.rider_driver_name.trim() || null,
            rider_driver_phone:
              editForm.rider_driver_phone.trim() || null,
            tracking_reference:
              editForm.tracking_reference.trim(),
            notes:
              editForm.notes.trim() || null,
          }),
        },
      )

      setItem(updated)
      setEditing(false)
      setMessage('Delivery details updated.')
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to update delivery.',
      )
    } finally {
      setBusy(false)
    }
  }


  const transition = async (status: string) => {
    if (!item) return

    setBusy(true)
    setError('')
    setMessage('')

    try {
      const updated = await api<Delivery>(
        businessPath(
          `/deliveries/${item.id}/status`,
        ),
        {
          method: 'POST',
          body: JSON.stringify({
            status,
            note: transitionNote.trim() || null,
          }),
        },
      )

      setItem(updated)
      setTransitionNote('')
      setMessage(
        `Delivery marked ${status.replaceAll('_', ' ')}.`,
      )

      const historyRows = await api<History[]>(
        businessPath(
          `/deliveries/${item.id}/history`,
        ),
      )

      setHistory(historyRows)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to update delivery status.',
      )
    } finally {
      setBusy(false)
    }
  }


  const selectCustomer = (customerId: string) => {
    const selected = customer(customerId)

    setForm({
      ...form,
      customer_id: customerId,
      sale_id: '',
      address:
        form.address ||
        selected?.address ||
        '',
    })
  }


  const clearFilters = () => {
    setStatusFilter('')
    setBranchFilter('')
    setCustomerFilter('')
    setStartDate('')
    setEndDate('')

    setTimeout(() => {
      void loadList()
    }, 0)
  }


  if (id && loading && !item) {
    return (
      <State title="Loading delivery?">
        {error || undefined}
      </State>
    )
  }


  if (id && item) {
    const selectedCustomer = customer(item.customer_id)
    const linkedSale = sale(item.sale_id)

    const nextStatuses =
      item.status === 'PENDING'
        ? ['IN_TRANSIT', 'CANCELLED']
        : item.status === 'IN_TRANSIT'
          ? ['DELIVERED', 'CANCELLED']
          : []

    return (
      <section className="aad-page">
        <PageHeader
          title={item.tracking_reference}
          description={`Delivery for ${
            selectedCustomer?.name ?? 'customer'
          }`}
          action={
            <div className="header-actions">
              {item.status === 'PENDING' && (
                <button
                  className="aad-button aad-button-gold"
                  type="button"
                  onClick={() =>
                    setEditing((current) => !current)
                  }
                >
                  {editing ? 'Cancel edit' : 'Edit delivery'}
                </button>
              )}

              <Link
                className="aad-button aad-button-secondary"
                to="/deliveries"
              >
                Back to deliveries
              </Link>
            </div>
          }
        />

        {error && (
          <p className="aad-error" role="alert">
            {error}
          </p>
        )}

        {message && (
          <p className="aad-success">
            {message}
          </p>
        )}

        <div className="detail-grid">
          <article className="aad-card aad-card-padding">
            <Badge value={item.status} />

            <h2 className="aad-section-title">
              Delivery details
            </h2>

            <p>
              <strong>Customer:</strong>{' '}
              {selectedCustomer?.name ??
                item.customer_id}
            </p>

            {selectedCustomer?.phone && (
              <p>
                <strong>Customer phone:</strong>{' '}
                {selectedCustomer.phone}
              </p>
            )}

            <p>
              <strong>Branch:</strong>{' '}
              {branchName(item.branch_id)}
            </p>

            <p>
              <strong>Address:</strong>{' '}
              {item.address}
            </p>

            <p>
              <strong>Delivery fee:</strong>{' '}
              {money(item.delivery_fee)}
            </p>

            <p>
              <strong>Rider / driver:</strong>{' '}
              {item.rider_driver_name || '?'}
            </p>

            <p>
              <strong>Rider phone:</strong>{' '}
              {item.rider_driver_phone || '?'}
            </p>

            <p>
              <strong>Notes:</strong>{' '}
              {item.notes || '?'}
            </p>

            {linkedSale && (
              <p>
                <strong>Sale:</strong>{' '}
                <Link to={`/sales/${linkedSale.id}`}>
                  {linkedSale.receipt_number}
                </Link>
              </p>
            )}

            <p>
              <strong>Created:</strong>{' '}
              {dateTime(item.created_at)}
            </p>

            <p>
              <strong>Last updated:</strong>{' '}
              {dateTime(item.updated_at)}
            </p>
          </article>

          <aside className="aad-card aad-card-padding">
            <h2 className="aad-section-title">
              Delivery status
            </h2>

            {!nextStatuses.length ? (
              <State
                title={
                  item.status === 'DELIVERED'
                    ? 'Delivery completed'
                    : 'Delivery closed'
                }
              />
            ) : (
              <>
                <label className="aad-field">
                  <span className="aad-label">
                    Status note
                  </span>

                  <textarea
                    className="aad-input"
                    value={transitionNote}
                    onChange={(event) =>
                      setTransitionNote(
                        event.target.value,
                      )
                    }
                    placeholder="Optional note for this status change"
                  />
                </label>

                <div className="form-actions">
                  {nextStatuses.map((status) => (
                    <button
                      type="button"
                      key={status}
                      className={
                        status === 'CANCELLED'
                          ? 'aad-button aad-button-secondary'
                          : 'aad-button aad-button-gold'
                      }
                      disabled={busy}
                      onClick={() =>
                        void transition(status)
                      }
                    >
                      {status.replaceAll('_', ' ')}
                    </button>
                  ))}
                </div>
              </>
            )}
          </aside>
        </div>

        {editing && item.status === 'PENDING' && (
          <form
            className="aad-card aad-card-padding"
            onSubmit={saveEdit}
          >
            <h2 className="aad-section-title">
              Edit pending delivery
            </h2>

            <div className="aad-form-grid">
              <label className="aad-field">
                <span className="aad-label">
                  Address
                </span>

                <input
                  className="aad-input"
                  required
                  value={editForm.address}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      address: event.target.value,
                    })
                  }
                />
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Delivery fee
                </span>

                <input
                  className="aad-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.delivery_fee}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      delivery_fee:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Tracking reference
                </span>

                <input
                  className="aad-input"
                  required
                  value={
                    editForm.tracking_reference
                  }
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      tracking_reference:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Rider / driver
                </span>

                <input
                  className="aad-input"
                  value={
                    editForm.rider_driver_name
                  }
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      rider_driver_name:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Rider / driver phone
                </span>

                <input
                  className="aad-input"
                  value={
                    editForm.rider_driver_phone
                  }
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      rider_driver_phone:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label className="aad-field">
                <span className="aad-label">
                  Notes
                </span>

                <textarea
                  className="aad-input"
                  value={editForm.notes}
                  onChange={(event) =>
                    setEditForm({
                      ...editForm,
                      notes: event.target.value,
                    })
                  }
                />
              </label>
            </div>

            <div className="form-actions">
              <button
                className="aad-button aad-button-gold"
                disabled={busy}
              >
                {busy
                  ? 'Saving?'
                  : 'Save delivery'}
              </button>

              <button
                type="button"
                className="aad-button aad-button-secondary"
                onClick={() => setEditing(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <section className="aad-card aad-card-padding">
          <h2 className="aad-section-title">
            Status history
          </h2>

          {!history.length ? (
            <State title="No delivery history" />
          ) : (
            <div className="aad-table-wrap">
              <table className="aad-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Previous</th>
                    <th>New status</th>
                    <th>Note</th>
                  </tr>
                </thead>

                <tbody>
                  {history.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        {dateTime(
                          entry.changed_at,
                        )}
                      </td>

                      <td>
                        {entry.previous_status
                          ? (
                            <Badge
                              value={
                                entry.previous_status
                              }
                            />
                          )
                          : 'Created'}
                      </td>

                      <td>
                        <Badge
                          value={
                            entry.new_status
                          }
                        />
                      </td>

                      <td>
                        {entry.note || '?'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </section>
    )
  }


  return (
    <section className="aad-page">
      <PageHeader
        title="Deliveries"
        description="Create deliveries, assign riders and track each order from pending to delivered."
      />

      {error && (
        <p className="aad-error" role="alert">
          {error}
        </p>
      )}

      {message && (
        <p className="aad-success">
          {message}
        </p>
      )}

      <form
        className="aad-card aad-card-padding"
        onSubmit={create}
      >
        <h2 className="aad-section-title">
          Create delivery
        </h2>

        <div className="aad-form-grid">
          <label className="aad-field">
            <span className="aad-label">
              Branch
            </span>

            <select
              className="aad-select"
              required
              value={form.branch_id}
              onChange={(event) =>
                setForm({
                  ...form,
                  branch_id: event.target.value,
                })
              }
            >
              <option value="">
                Select branch
              </option>

              {branches.map((branch) => (
                <option
                  key={branch.id}
                  value={branch.id}
                >
                  {branch.name}
                </option>
              ))}
            </select>
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Customer
            </span>

            <select
              className="aad-select"
              required
              value={form.customer_id}
              onChange={(event) =>
                selectCustomer(
                  event.target.value,
                )
              }
            >
              <option value="">
                Select customer
              </option>

              {customers.map((entry) => (
                <option
                  key={entry.id}
                  value={entry.id}
                >
                  {entry.name}
                  {entry.phone
                    ? ` ? ${entry.phone}`
                    : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Linked sale
            </span>

            <select
              className="aad-select"
              value={form.sale_id}
              onChange={(event) =>
                setForm({
                  ...form,
                  sale_id: event.target.value,
                })
              }
            >
              <option value="">
                No linked sale
              </option>

              {customerSales.map((entry) => (
                <option
                  key={entry.id}
                  value={entry.id}
                >
                  {entry.receipt_number} ?{' '}
                  {money(entry.total)}
                </option>
              ))}
            </select>
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Delivery address
            </span>

            <input
              className="aad-input"
              required
              value={form.address}
              onChange={(event) =>
                setForm({
                  ...form,
                  address: event.target.value,
                })
              }
            />
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Delivery fee
            </span>

            <input
              className="aad-input"
              type="number"
              min="0"
              step="0.01"
              value={form.delivery_fee}
              onChange={(event) =>
                setForm({
                  ...form,
                  delivery_fee:
                    event.target.value,
                })
              }
            />
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Tracking reference
            </span>

            <input
              className="aad-input"
              required
              placeholder="e.g. AAD-DEL-001"
              value={
                form.tracking_reference
              }
              onChange={(event) =>
                setForm({
                  ...form,
                  tracking_reference:
                    event.target.value,
                })
              }
            />
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Rider / driver name
            </span>

            <input
              className="aad-input"
              value={
                form.rider_driver_name
              }
              onChange={(event) =>
                setForm({
                  ...form,
                  rider_driver_name:
                    event.target.value,
                })
              }
            />
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Rider / driver phone
            </span>

            <input
              className="aad-input"
              value={
                form.rider_driver_phone
              }
              onChange={(event) =>
                setForm({
                  ...form,
                  rider_driver_phone:
                    event.target.value,
                })
              }
            />
          </label>

          <label className="aad-field">
            <span className="aad-label">
              Notes
            </span>

            <textarea
              className="aad-input"
              value={form.notes}
              onChange={(event) =>
                setForm({
                  ...form,
                  notes: event.target.value,
                })
              }
            />
          </label>
        </div>

        <div className="form-actions">
          <button
            className="aad-button aad-button-gold"
            disabled={busy}
          >
            {busy
              ? 'Creating?'
              : 'Create delivery'}
          </button>
        </div>
      </form>

      <section className="aad-card aad-card-padding">
        <h2 className="aad-section-title">
          Delivery filters
        </h2>

        <div className="aad-form-grid">
          <select
            className="aad-select"
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(
                event.target.value,
              )
            }
          >
            <option value="">
              All statuses
            </option>

            {statuses.map((status) => (
              <option
                key={status}
                value={status}
              >
                {status.replaceAll('_', ' ')}
              </option>
            ))}
          </select>

          <select
            className="aad-select"
            value={branchFilter}
            onChange={(event) =>
              setBranchFilter(
                event.target.value,
              )
            }
          >
            <option value="">
              All accessible branches
            </option>

            {branches.map((branch) => (
              <option
                key={branch.id}
                value={branch.id}
              >
                {branch.name}
              </option>
            ))}
          </select>

          <select
            className="aad-select"
            value={customerFilter}
            onChange={(event) =>
              setCustomerFilter(
                event.target.value,
              )
            }
          >
            <option value="">
              All customers
            </option>

            {customers.map((entry) => (
              <option
                key={entry.id}
                value={entry.id}
              >
                {entry.name}
              </option>
            ))}
          </select>

          <input
            className="aad-input"
            type="date"
            value={startDate}
            onChange={(event) =>
              setStartDate(event.target.value)
            }
          />

          <input
            className="aad-input"
            type="date"
            value={endDate}
            onChange={(event) =>
              setEndDate(event.target.value)
            }
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="aad-button aad-button-gold"
            disabled={loading}
            onClick={() => void loadList()}
          >
            Apply filters
          </button>

          <button
            type="button"
            className="aad-button aad-button-secondary"
            disabled={loading}
            onClick={clearFilters}
          >
            Clear filters
          </button>
        </div>
      </section>

      {loading ? (
        <State title="Loading deliveries?" />
      ) : !items.length ? (
        <State title="No deliveries">
          Create a delivery or change your filters.
        </State>
      ) : (
        <div className="aad-table-wrap">
          <table className="aad-table">
            <thead>
              <tr>
                <th>Tracking</th>
                <th>Customer</th>
                <th>Branch</th>
                <th>Address</th>
                <th>Fee</th>
                <th>Rider</th>
                <th>Status</th>
                <th>Created</th>
                <th>View</th>
              </tr>
            </thead>

            <tbody>
              {items.map((delivery) => (
                <tr key={delivery.id}>
                  <td>
                    <strong>
                      {
                        delivery.tracking_reference
                      }
                    </strong>
                  </td>

                  <td>
                    {
                      customer(
                        delivery.customer_id,
                      )?.name ??
                      delivery.customer_id
                    }
                  </td>

                  <td>
                    {branchName(
                      delivery.branch_id,
                    )}
                  </td>

                  <td>{delivery.address}</td>

                  <td>
                    {money(
                      delivery.delivery_fee,
                    )}
                  </td>

                  <td>
                    {
                      delivery.rider_driver_name ||
                      '?'
                    }
                  </td>

                  <td>
                    <Badge
                      value={
                        delivery.status
                      }
                    />
                  </td>

                  <td>
                    {dateTime(
                      delivery.created_at,
                    )}
                  </td>

                  <td>
                    <Link
                      className="aad-button aad-button-secondary"
                      to={`/deliveries/${delivery.id}`}
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
